import React, { useCallback } from "react";
import { createPortal } from "react-dom";

import { PopoverContainer, PopoverContent } from "./styled";
import type { PopoverProps } from "./types";

export const Popover: React.FC<PopoverProps> = ({
  trigger,
  children,
  open,
  defaultOpen,
  onOpenChange,
  placement = "bottom-start",
  disabled: _disabled = false,
  className,
  testId,
  contentBackground,
  contentBorderColor,
  contentBorderRadius,
  contentPadding,
  contentMinWidth,
  contentMaxWidth,
  contentBoxShadow,
  contentZIndex,
  contentClassName,
  contentStyle,
  matchTriggerWidth = false,
}) => {
  const isControlled = typeof open === "boolean";
  const [internalOpen, setInternalOpen] = React.useState<boolean>(
    Boolean(defaultOpen),
  );
  const actualOpen = isControlled ? (open as boolean) : internalOpen;
  const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>(
    undefined,
  );
  const [triggerRect, setTriggerRect] = React.useState<DOMRect | null>(null);

  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLDivElement | null>(null);

  // Use portal for high z-index values to escape modal containers
  const usePortal = (contentZIndex ?? 1000) > 5000;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  // Calculate trigger width and position when opening
  React.useLayoutEffect(() => {
    if (actualOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      if (matchTriggerWidth) {
        setTriggerWidth(rect.width);
      }
      if (usePortal) {
        setTriggerRect(rect);
      }
    }
  }, [matchTriggerWidth, actualOpen, usePortal]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (actualOpen) {
      document.addEventListener("keydown", onKeyDown);
    }
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [actualOpen, setOpen]);

  // Calculate portal position
  const getPortalStyle = () => {
    if (!usePortal || !triggerRect) return {};

    const style: React.CSSProperties = {
      position: "fixed",
      zIndex: contentZIndex ?? 1000,
    };

    switch (placement) {
      case "bottom-start":
        style.top = triggerRect.bottom + 4;
        style.left = triggerRect.left;
        break;
      case "bottom-end":
        style.top = triggerRect.bottom + 4;
        style.right = window.innerWidth - triggerRect.right;
        break;
      case "top-start":
        style.bottom = window.innerHeight - triggerRect.top + 4;
        style.left = triggerRect.left;
        break;
      case "top-end":
        style.bottom = window.innerHeight - triggerRect.top + 4;
        style.right = window.innerWidth - triggerRect.right;
        break;
      default:
        style.top = triggerRect.bottom + 4;
        style.left = triggerRect.left;
    }

    if (matchTriggerWidth) {
      style.width = triggerRect.width;
    }

    return style;
  };

  const renderPopoverContent = () => (
    <>
      <div
        onClick={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(false);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close popover"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: (contentZIndex ?? 1000) - 1,
        }}
      />
      <PopoverContent
        $placement={usePortal ? "bottom-start" : placement}
        $contentBackground={contentBackground}
        $contentBorderColor={contentBorderColor}
        $contentBorderRadius={contentBorderRadius}
        $contentPadding={contentPadding}
        $contentMinWidth={contentMinWidth}
        $contentMaxWidth={contentMaxWidth}
        $contentBoxShadow={contentBoxShadow}
        $contentZIndex={contentZIndex}
        $matchTriggerWidth={matchTriggerWidth}
        $triggerWidth={triggerWidth}
        className={contentClassName}
        style={
          usePortal ? { ...getPortalStyle(), ...contentStyle } : contentStyle
        }
      >
        {children}
      </PopoverContent>
    </>
  );

  return (
    <PopoverContainer
      ref={wrapperRef}
      className={className}
      data-testid={testId}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={triggerRef}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={actualOpen}
      >
        {trigger}
      </div>
      {actualOpen &&
        (() => {
          if (usePortal && typeof document !== "undefined") {
            return triggerRect
              ? createPortal(renderPopoverContent(), document.body)
              : null;
          }
          return renderPopoverContent();
        })()}
    </PopoverContainer>
  );
};
